import { Navigate, Route, Routes } from 'react-router';

import { ProtectedRoute } from '@/components/AppRouter/ProtectedRoute';
import { UserLocaleSetup } from '@/components/AppRouter/UserLocaleSetup';

import { ROUTE } from '@/constants/routes';

import { useAppSelector } from '@/hooks/redux';

import ComingSoonPage from '@/pages/application-type/ComingSoonPage';
import AddAdultContactPage from '@/pages/application-type/desktop/AddAdultContactPage';
import AddAdultDetailPage from '@/pages/application-type/desktop/AddAdultDetailPage';
import AddAdultDetailResultFailedPage from '@/pages/application-type/desktop/AddAdultDetailResultFailedPage';
import AddAdultDetailResultPage from '@/pages/application-type/desktop/AddAdultDetailResultPage';
import RightStartComingSoonPage from '@/pages/application-type/RightStartComingSoonPage';
import MinorAgeValidationFailedPage from '@/pages/onboarding/minor-age-validation';
import NonResidentMinorRejectedPage from '@/pages/onboarding/non-resident-minor-rejected/NonResidentMinorRejectedPage';
import ReviewPersonalDetailsPage from '@/pages/personal-details/ReviewPersonalDetailsPage';
import UpdatePersonalInfoPage from '@/pages/personal-details/UpdatePersonalInfoPage';

import {
  selectNewApplicationTypeSelectionFeature,
  selectRightStartFeature,
} from '@/redux/configs';

import { DocumentType } from '@/types/document';
import { ApplicationType } from '@/types/user';

import AccountTypePage from '../../account-type/desktop/AccountTypePage';
import BasicInfoPage from '../../account/basic-information/BasicInfoPage';
import ApplicationTypeSelectionPage from '../../application-type/ApplicationTypeSelectionPage';
import ApplicationTypeSelectionFailedPage from '../../application-type/desktop/ApplicationTypeSelectionFailedPage';
import AddressPage from '../../onboarding/address-information/AddressPage';
import AgeValidationFailedPage from '../../onboarding/age-validation/AgeValidationFailedPage';
import CaymanCountryValidationFailedPage from '../../onboarding/cayman-country-resident-validation/CaymanCountryValidationFailedPage';
import JointAdultAgeValidationFailedPage from '../../onboarding/joint-adult-age-validation/JointAdultAgeValidationFailedPage';
import MinorBasicInfoPage from '../../onboarding/minor-basic-information/MinorBasicInfoPage';
import FinishedPage from '../../onboarding/success/FinishedPage';
import CaymanThirdPartyComplianceCheckFailedPage from '../../third-party-compliance-check/desktop/CaymanThirdPartyComplianceCheckFailedPage';
import ThirdPartyComplianceCheckFailedPage from '../../third-party-compliance-check/desktop/ThirdPartyComplianceCheckFailedPage';
import ThirdPartyComplianceCheckPage from '../../third-party-compliance-check/desktop/ThirdPartyComplianceCheckPage';
import AccountRejectedPage from '../account-status/AccountRejectedPage';
import MoreInformationPage from '../more-information/MoreInformationPage';
import PhonePage from '../phone/PhonePage';
import MinorReviewCompletePage from '../verification/MinorReviewCompletePage';
import VerifyIDPage from '../verification/VerifyIDPage';
import VerifyMinorBirthCertificatePage from '../verification/VerifyMinorBirthCertificatePage';
import { VerifyMinorIDPage } from '../verification/VerifyMinorIDPage';
import VerifySelfiePage from '../verification/VerifySelfiePage';

export function AuthenticatedPage() {
  const enabledRightStartAccount = useAppSelector(selectRightStartFeature);
  const enabledNewApplicationTypeSelection = useAppSelector(
    selectNewApplicationTypeSelectionFeature,
  );

  return (
    <UserLocaleSetup>
      <Routes>
        <Route path={ROUTE.HOME} element={<ProtectedRoute />} />
        <Route
          path={ROUTE.PHONE_FORM}
          element={
            <ProtectedRoute>
              <PhonePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.VERIFY_ID}
          element={
            <ProtectedRoute>
              <VerifyIDPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTE.VERIFY_MINOR_ID}
          element={
            <ProtectedRoute>
              <VerifyMinorIDPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.VERIFY_SELFIE}
          element={
            <ProtectedRoute>
              <VerifySelfiePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.VERIFY_MINOR_BIRTH_CERTIFICATE}
          element={
            <ProtectedRoute>
              <VerifyMinorBirthCertificatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.VERIFY_MINOR_FAMILY_BOOK}
          element={
            <ProtectedRoute>
              <VerifyMinorBirthCertificatePage
                documentType={DocumentType.FamilyBook}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.MINOR_REVIEW_COMPLETE}
          element={
            <ProtectedRoute>
              <MinorReviewCompletePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.MINOR_BASIC_INFO}
          element={
            <ProtectedRoute>
              <MinorBasicInfoPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.BASIC_INFO}
          element={
            <ProtectedRoute>
              <BasicInfoPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.ADDRESS_INFORMATION}
          element={
            <ProtectedRoute>
              <AddressPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.SUCCESS}
          element={
            <ProtectedRoute>
              <FinishedPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.ACCOUNT_TYPE}
          element={
            <ProtectedRoute>
              <AccountTypePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.ACCOUNT_REJECTED}
          element={<AccountRejectedPage />}
        />
        <Route
          path={ROUTE.MORE_INFORMATION}
          element={
            <ProtectedRoute>
              <MoreInformationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.REVIEW_PERSONAL_DETAILS}
          element={
            <ProtectedRoute>
              <ReviewPersonalDetailsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.UPDATE_PERSONAL_DETAILS}
          element={
            <ProtectedRoute>
              <UpdatePersonalInfoPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.AGE_VALIDATION_FAILURE}
          element={<AgeValidationFailedPage />}
        />
        <Route
          path={ROUTE.THIRD_PARTY_COMPLIANCE_CHECK}
          element={
            <ProtectedRoute>
              <ThirdPartyComplianceCheckPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTE.THIRD_PARTY_COMPLIANCE_CHECK_FAILED}
          element={<ThirdPartyComplianceCheckFailedPage />}
        />
        <Route
          path={ROUTE.CAYMAN_COUNTRY_VALIDATION_FAILURE}
          element={<CaymanCountryValidationFailedPage />}
        />
        <Route
          path={ROUTE.CAYMAN_THIRD_PARTY_COMPLIANCE_CHECK_FAILED}
          element={<CaymanThirdPartyComplianceCheckFailedPage />}
        />
        {enabledNewApplicationTypeSelection && (
          <>
            <Route
              path={ROUTE.APPLICATION_TYPE_SELECTION}
              element={
                <ProtectedRoute>
                  <ApplicationTypeSelectionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTE.APPLICATION_TYPE_SELECTION_FAILED}
              element={<ApplicationTypeSelectionFailedPage />}
            />
            <Route
              path={ROUTE.RIGHT_START_COMING_SOON}
              element={
                <ProtectedRoute>
                  <RightStartComingSoonPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTE.BUSINESS_COMING_SOON}
              element={
                <ProtectedRoute>
                  <ComingSoonPage type={ApplicationType.BUSINESS} />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTE.SHARED_WITH_SOMEONE_ELSE_COMING_SOON}
              element={
                <ProtectedRoute>
                  <ComingSoonPage
                    type={ApplicationType.SHARED_WITH_SOMEONE_ELSE}
                  />
                </ProtectedRoute>
              }
            />
          </>
        )}
        {enabledRightStartAccount && (
          <>
            <Route
              path={ROUTE.JOINT_ADULT_AGE_VALIDATION_FAILURE}
              element={<JointAdultAgeValidationFailedPage />}
            />
            <Route
              path={ROUTE.MINOR_AGE_VALIDATION_FAILURE}
              element={<MinorAgeValidationFailedPage />}
            />
            <Route
              path={ROUTE.NON_RESIDENT_MINOR_REJECTED}
              element={<NonResidentMinorRejectedPage />}
            />
            <Route
              path={ROUTE.ADD_ADULT_DETAIL_RESULT}
              element={
                <ProtectedRoute>
                  <AddAdultDetailResultPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTE.ADD_ADULT_DETAIL_RESULT_FAILED}
              element={<AddAdultDetailResultFailedPage />}
            />
            <Route
              path={ROUTE.ADD_ADULT_DETAIL}
              element={
                <ProtectedRoute>
                  <AddAdultDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTE.ADD_ADULT_CONTACT}
              element={
                <ProtectedRoute>
                  <AddAdultContactPage />
                </ProtectedRoute>
              }
            />
          </>
        )}
        <Route path="*" element={<Navigate to={ROUTE.HOME} />} />
      </Routes>
    </UserLocaleSetup>
  );
}
